import type { NextAction } from "../api/types.js";
import { normalise } from "./drafter.js";

/**
 * `ReadQuestions` — the M1 stand-in for the drafting model's read-only intents
 * (T0: no money moves, no signature). Like the fixture drafter, it holds a short
 * list of REVIEWED questions and matches them exactly after normalising case,
 * punctuation and number words; anything else is not a read, so a payment is
 * never mistaken for one and nothing is guessed. The contract's `query_kind`
 * has no spend or bill-due kind yet (README, ask x), so these are the client's
 * own until the model and the contract carry them.
 */
/** DCTA-204 (R3, R4): a read the bank answered carries its own words — its line for the screen, the kind that speaks it. */
export interface BankWords {
  readonly line?: string;
  readonly speech?: string;
}

export type ReadAsk = (
  /** DCTA-203: `accountId` — the one account the bank names (DCTA-204 R1); none, every account. */
  | { readonly kind: "balance"; readonly accountId?: string }
  /** DCTA-202: `cardId` — the card the bank names. DCTA-203: `every` — the bank named none: every card its read lists. */
  | { readonly kind: "card_details"; readonly cardId?: string; readonly every?: true }
  /**
   * DCTA-203: `period` — the bank's own period key; none, the bank read's own ("this_month"). DCTA-206: `range` — the dates
   * the bank names instead of a period (A1); `categories` — the several categories one word stands for (A2), never beside
   * `category`.
   */
  | { readonly kind: "spend"; readonly category?: string; readonly categories?: readonly string[]; readonly period?: SpendPeriod; readonly range?: SpendRange }
  /** DCTA-208: `cardId` — the credit card picked from "Which card?" when two have a statement. */
  | { readonly kind: "bill_due"; readonly cardId?: string }
  /** DCTA-203: the reads the bank drafts that the app's own phrasings never reach. */
  | { readonly kind: "bills" }
  | { readonly kind: "limits" }
  /** DCTA-204 (R1): `accountId` — one account's recent activity, as the bank's read names it. */
  | { readonly kind: "recent"; readonly accountId?: string }
  | { readonly kind: "pending" }
  /** DCTA-209 (F61): what is scheduled — `destination`, the payee, biller or own account the bank's read names (B76). */
  | { readonly kind: "scheduled"; readonly destination?: string }
  /** DCTA-203: a read the bank answered that this app has no card for; DCTA-204 (R7): its way on. */
  | { readonly kind: "other"; readonly path: string; readonly ways?: readonly { readonly label: string; readonly action: NextAction }[] }
  | { readonly kind: "not_available"; readonly product: string }
) & {
  /** DCTA-204: the bank's own words — present on every read the bank drafted, absent on the app's own phrasings. */
  readonly bank?: BankWords;
};

/** The spend read's own periods (contract `query.period`; lab/dcta SPEND_PERIODS). */
export const SPEND_PERIODS = ["this_month", "last_month", "last_7_days", "last_30_days"] as const;
export type SpendPeriod = (typeof SPEND_PERIODS)[number];
/** DCTA-206 (A1): a spend read's own dates — inclusive Singapore calendar days, as the bank's read names them. */
export interface SpendRange {
  readonly from: string;
  readonly to: string;
}

const REVIEWED: readonly (readonly [string, ReadAsk])[] = [
  ["What's my balance?", { kind: "balance" }],
  ["What is my balance?", { kind: "balance" }],
  ["How much money do I have?", { kind: "balance" }],
  ["Show my balances", { kind: "balance" }],
  ["Show my card details", { kind: "card_details" }],
  ["What's my card number?", { kind: "card_details" }],
  ["How much did I spend this month?", { kind: "spend" }],
  ["How much did I spend on groceries this month?", { kind: "spend", category: "groceries" }],
  ["How much did I spend on transfers this month?", { kind: "spend", category: "transfers" }],
  ["When is my card bill due?", { kind: "bill_due" }],
  ["When is my credit card bill due?", { kind: "bill_due" }],
  ["How much is my card bill?", { kind: "bill_due" }],
  ["How much is in my fixed deposit?", { kind: "not_available", product: "Fixed Deposit" }],
];

export class ReadQuestions {
  readonly #byKey = new Map(REVIEWED.map(([q, ask]) => [normalise(q), ask]));

  match(utterance: string): ReadAsk | undefined {
    return this.#byKey.get(normalise(utterance));
  }

  questions(): readonly string[] {
    return REVIEWED.map(([q]) => q);
  }
}
